import { List } from "immutable";

import { ICheckable } from "./ICheckable";

class IndeterminateCheckboxState {
    /**
     * Indicates whether the parent checkbox is checked.
     * Typically true if all child checkboxes are selected.
     */
    public parentChecked: boolean;
    /**
     * Indicates whether the parent checkbox is in an indeterminate state.
     * Typically true if some, but not all, child checkboxes are selected.
     */
    public parentIndeterminate: boolean;

    constructor(parentChecked: boolean, parentIndeterminate: boolean) {
        this.parentChecked = parentChecked;
        this.parentIndeterminate = parentIndeterminate;
    }

    /**
     * Computes the checkbox state based on a list of checkable items.
     * @param items A list of items that implement the ICheckable interface.
     * @returns A new instance of IndeterminateCheckboxState with the computed state.
     */
    public static fromItems(items: List<ICheckable>): IndeterminateCheckboxState {
        const total = items.count();
        if (total === 0) {
            return new IndeterminateCheckboxState(false, false);
        }
        const selected = items.filter(item => item.Checked).count();
        const parentChecked = selected === total;
        const parentIndeterminate = selected > 0 && selected < total;
        return new IndeterminateCheckboxState(parentChecked, parentIndeterminate);
    }

    /**
     * @param items A list of items that implement the ICheckable interface.
     * @returns Determines whether all items are checked or none are checked.
     */
    public static getAllCheckboxStates(items: List<ICheckable>): { allChecked: boolean; noneChecked: boolean } {
        const allChecked = items.every(item => item.Checked);
        const noneChecked = items.every(item => !item.Checked);
        return { allChecked, noneChecked };
    }
}

export { IndeterminateCheckboxState };
